import {
  Avatar,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import { CalendarDate } from '@internationalized/date';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { OpportunityViewModal } from '../../component/opportunities.tsx/view/opportunityViewModal';
import ViewModal from '../../component/readyRoster/view/viewModal';
import { DetailedAbuseReportResponseDto, MarketplaceCardResponseDto } from '../../types/types';
import { formatCardType, formatDateTime } from '../../utils/formatUtils';

export type ReportGroup = {
  cardId: string;
  cardType: string;
  reports: DetailedAbuseReportResponseDto[];
};

export type ViewModalState = {
  isOpen: boolean;
  type: 'READYROSTERCARD' | null;
  cardId: string | null;
  cardData: MarketplaceCardResponseDto | null;
  confirmPublishPopup: boolean;
  confirmContactPopup: boolean;
  showReportPopup: boolean;
};

export type OpportunityModalState = {
  isOpen: boolean;
  cardId: string | null;
};


export const getUserUsername = (): string => {
  const userJson = localStorage.getItem('loginResponse');
  const user = userJson ? JSON.parse(userJson) : null;
  return user?.username || '';
};

export const getInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName ? firstName.charAt(0) : '';
  const last = lastName ? lastName.charAt(0) : '';
  return `${first}${last}`.toUpperCase();
};

export const groupReportsByCard = (reports: DetailedAbuseReportResponseDto[]): ReportGroup[] => {
  const groups: Record<string, ReportGroup> = {};

  reports.forEach((report) => {
    let cardId = '';
    let cardType = report.type;

    if (report.marketplaceCardResponseDto?.id) {
      cardId = String(report.marketplaceCardResponseDto.id);
      cardType = 'MARKETPLACECARD';
    } else if (report.postResponseDto?.id) {
      cardId = String(report.postResponseDto.id);
      cardType = 'POST';
    } else if (report.reportedTo?.id && report.type === 'PROFILE') {
      cardId = String(report.reportedTo.id);
      cardType = 'PROFILE';
    } else {
      cardId = String(report.id);
    }

    if (!groups[cardId]) {
      groups[cardId] = {
        cardId,
        cardType: cardType,
        reports: [],
      };
    }
    groups[cardId].reports.push(report);
  });

  return Object.values(groups);
};

export const convertAvailableDaysToSet = (availableDays: string[] | undefined): Set<string> => {
  if (!availableDays || !Array.isArray(availableDays)) {
    return new Set<string>();
  }

  return new Set(availableDays.map(day => {
    if (day.includes('T')) {
      return day.split('T')[0];
    }
    return day;
  }));
};

export const getViewButtonText = (cardType: string): string => {
  switch (cardType) {
    case 'POST':
      return 'View Post';
    case 'PROFILE':
      return 'View Profile';
    case 'MARKETPLACECARD':
      return 'View Card';
    default:
      return 'View';
  }
};


interface AbuseReportTableProps {
  reportsGrouped: ReportGroup[];
  loading: boolean;
  cardLoading: boolean;
  onViewReports: (group: ReportGroup, event: React.MouseEvent) => void;
  onViewCard: (group: ReportGroup, event: React.MouseEvent) => void;
  onRowClick: (group: ReportGroup) => void;
}

export const AbuseReportTable: React.FC<AbuseReportTableProps> = ({
  reportsGrouped,
  loading,
  cardLoading,
  onViewReports,
  onViewCard,
  onRowClick,
}) => {
  const { t } = useTranslation('abusereport');

  if (loading) {
    return (
      <div className="p-4">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-4 p-4 border-b border-gray-100">
              <div className="w-10 h-10 bg-gray-200 rounded-full shimmer"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded shimmer w-1/4"></div>
                <div className="h-3 bg-gray-200 rounded shimmer w-1/3"></div>
              </div>
              <div className="h-4 bg-gray-200 rounded shimmer w-20"></div>
              <div className="h-4 bg-gray-200 rounded shimmer w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <Table
        aria-label="Abuse Reports Grouped by Card"
        className="min-w-full"
        removeWrapper
        classNames={{
          wrapper: 'overflow-auto custom-scrollbar',
          table: 'min-w-full',
        }}
      >
        <TableHeader>
          <TableColumn className="text-left text-xs font-small text-gray-500 min-w-[250px]">
            {t('tableReportedCard')}
          </TableColumn>
          <TableColumn className="text-left text-xs font-small text-gray-500 min-w-[120px]">
            {t('tableReportsCount')}
          </TableColumn>
          <TableColumn className="text-left text-xs font-small text-gray-500 min-w-[160px]">
            {t('tableLastReportedAt')}
          </TableColumn>
          <TableColumn className="text-left text-xs font-small text-gray-500 min-w-[180px]">
            {t('tableAction')}
          </TableColumn>
        </TableHeader>
        <TableBody
          items={reportsGrouped.map((group) => ({
            key: group.cardId,
            group,
          }))}
          emptyContent={t('noReportsFound')}
        >
          {(item) => {
            const group = item.group;
            const lastReportedAt = formatDateTime(
              group.reports
                .map((r) => r.reportedAt)
                .reduce((latest, current) =>
                  new Date(current).getTime() > new Date(latest).getTime() ? current : latest
                )
            );
            const reportedTo = group.reports[0].reportedTo;

            return (
              <TableRow
                key={item.key}
                className="cursor-pointer hover:bg-gray-50 border-b border-gray-100 fade-in"
                onClick={() => onRowClick(group)}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar
                      src={reportedTo?.profileImage}
                      alt={`${reportedTo?.firstName || ''} ${reportedTo?.lastName || ''}`}
                      name={getInitials(reportedTo?.firstName, reportedTo?.lastName)}
                      size="sm"
                      className="flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 text-sm truncate">
                        {formatCardType(group.cardType)}
                      </div>
                      <div className="text-xs text-gray-700 truncate">
                        Creator: {reportedTo?.firstName || ''} {reportedTo?.lastName || ''}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-gray-700">{group.reports.length}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-gray-700">{lastReportedAt}</div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      color="primary"
                      size="sm"
                      variant="flat"
                      onClick={(e) => onViewReports(group, e)}
                    >
                      View Reports
                    </Button>
                    <Button
                      color="secondary"
                      size="sm"
                      variant="flat"
                      onClick={(e) => onViewCard(group, e)}
                      isLoading={cardLoading}
                      disabled={cardLoading}
                    >
                      {getViewButtonText(group.cardType)}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          }}
        </TableBody>
      </Table>
    </div>
  );
};

interface AbuseReportModalsProps {
  selectedGroup: ReportGroup | null;
  viewModal: ViewModalState;
  opportunityModal: OpportunityModalState;
  calendarMonth: CalendarDate;
  setCalendarMonth: (date: CalendarDate) => void;
  onCloseReportModal: () => void;
  onCloseViewModal: () => void;
  onCloseOpportunityModal: () => void;
  setViewModal: React.Dispatch<React.SetStateAction<ViewModalState>>;
}

export const AbuseReportModals: React.FC<AbuseReportModalsProps> = ({
  selectedGroup,
  viewModal,
  opportunityModal,
  calendarMonth,
  setCalendarMonth,
  onCloseReportModal,
  onCloseViewModal,
  onCloseOpportunityModal,
  setViewModal,
}) => {
  const { t } = useTranslation('abusereport');

  return (
    <>
      {selectedGroup && (
        <Modal
          isOpen={!!selectedGroup}
          onClose={onCloseReportModal}
          className="max-w-lg"
          backdrop="blur"
          scrollBehavior="inside"
        >
          <ModalContent>
            <ModalHeader className="text-lg font-semibold text-gray-800">
              {t('modalReportsFor', { type: formatCardType(selectedGroup.cardType) })}
            </ModalHeader>
            <ModalBody>
              <div className="space-y-4">
                {selectedGroup.reports.map((report) => (
                  <div
                    key={report.id}
                    className="border border-gray-200 p-4 rounded-lg shadow-sm bg-white"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <Avatar
                        src={report.reportedBy.profileImage}
                        alt={`${report.reportedBy.firstName || ''} ${report.reportedBy.lastName || ''}`}
                        name={getInitials(report.reportedBy.firstName, report.reportedBy.lastName)}
                        size="sm"
                        className="flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 text-sm">
                          {t('reporterLabel')} {report.reportedBy.firstName || ''}{' '}
                          {report.reportedBy.lastName || ''}
                        </div>
                        <div className="text-sm text-gray-700">
                          <strong>{t('emailLabel')}</strong> {report.reportedBy.email || 'N/A'}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-700">
                      <strong>{t('reasonLabel')}</strong> {report.reason}
                    </div>
                    <div className="text-sm text-gray-700">
                      <strong>{t('createdAtLabel')}</strong>{' '}
                      {new Date(report.reportedAt).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button size="sm" variant="flat" color="primary" onClick={onCloseReportModal}>
                {t('closeButton')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {viewModal.isOpen && viewModal.cardData && (
        <ViewModal
          isOpen={viewModal.isOpen}
          onClose={onCloseViewModal}
          viewCard={viewModal.cardData}
          marketplaceId={viewModal.cardId || undefined}
          role="ADMIN"
          activeTab="all"
          loggedInUserProfileType="ADMIN"
          loggedInUserName=""
          currentMonth={calendarMonth}
          setCurrentMonth={setCalendarMonth}
          selectedDates={convertAvailableDaysToSet(
            viewModal.cardData.readyRosterCard?.availableDays
          )}
          isPublishing={false}
          confirmPublishPopup={viewModal.confirmPublishPopup}
          setConfirmPublishPopup={(open) =>
            setViewModal((prev) => ({ ...prev, confirmPublishPopup: open }))
          }
          confirmContactPopup={viewModal.confirmContactPopup}
          setConfirmContactPopup={(open) =>
            setViewModal((prev) => ({ ...prev, confirmContactPopup: open }))
          }
          showReportPopup={viewModal.showReportPopup}
          setShowReportPopup={(open) =>
            setViewModal((prev) => ({ ...prev, showReportPopup: open }))
          }
          handlePublish={(cardId: string) => {
            console.log('Publish card:', cardId);
          }}
          handleCreateContact={(cardId: any, e: any) => {
            console.log('Create contact for card:', cardId);
          }}
        />
      )}

      <OpportunityViewModal
        isOpen={opportunityModal.isOpen}
        onClose={onCloseOpportunityModal}
        cardId={opportunityModal.cardId}
        activeTab="all"
      />
    </>
  );
};